import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {validateFields, validateField} from '../../../utilities/validation/validator';
import {regexPatterns} from '../../../utilities/validation/validationVariables';
import {createOtpLogin, validateOtpLogin, saveLoginData, determineLoginType} from '../../../utilities/authentication/authenticationSlice';
import lodash from 'lodash';
import otpImages from './images/images';
import {otpReportData, otpCreateReport} from './otpReports';
import ProviderManager from '../../../provider/providerManager';
import {hideAppLoader, showAppLoader} from 'app/Components/Shared/Loader/LoaderSlice';
import authenticationProvider from 'app/utilities/authentication/authenticationProvider';
import Config from 'app/Config';
import {rolesKeys} from 'app/Components/Block/OTPLoginBlock/Assets/variables';

export const otpSteps = {
  PHONE: 'phone',
  OTP: 'otp',
  AFTER_OTP: 'afterOtp',
};
export const createNewOtpButtonList = [
  {id: 'sms', alt: `הקוד לא הגיע,<br/> שלחו שוב`, description: `הקוד לא הגיע,<br/> שלחו שוב`, icon: otpImages.sms},
  {id: 'voice', alt: `שלחו את הקוד שוב<br/> בהודעה קולית`, description: `שלחו את הקוד שוב<br/> בהודעה קולית`, icon: otpImages.voice},
];

const otpInitialPhoneForm = {
  phone: '',
  id: '',
  valid: '',
  errors: {
    phone: '',
  },
  rules: {},
};

const otpInitialCodeFormState = {
  verifyCode: '',
  valid: '',
  errors: {},
  rules: {
    verifyCode: {
      required: {
        value: true,
        error: 'לא הוזן קוד',
      },
      minLength: {
        value: 6,
        error: 'יש להזין 6 תווים',
      },
      pattern: {
        value: regexPatterns.numbersRegex,
        error: 'ערך לא תקין',
      },
    },
  },
};

const initialState = {
  loginData: null,
  currentStep: otpSteps.PHONE,
  otpIsLoading: false,
  otpPhoneForm: otpInitialPhoneForm,
  otpCodeForm: otpInitialCodeFormState,
  numOfAttempts: 0,
  wrongOtpMessage: '',
  reloadProcess: false,
  showWrongOtpCodeMessage: false,
  creationDate: '',
  deliveryType: '',
  step1Ticket: '',
  restrictedCodeRequired: false,
  restrictedCodeValue: '',
  termsInd: false,
  termsOfUseConfirmed: false,
  blockSource: '',
  otpErrorPage: false,
  code: null,
  terms: null,
  ticketId: null,
};

export const verifyOtpPhone = createAsyncThunk(
  'otpAuthentication/verifyOtpPhone',
  async ({phoneNumber, processType, otpOrigin, originProcess, loginType = '', genericFields = ''}, thunkApi) => {
    try {
      // LoginStep1 args: Subscriber, ProcessType, OtpOrigin, OriginProcess, LoginType, GenericFields
      return await ProviderManager.getProviderData('LoginStep1', [
        phoneNumber,
        processType || '',
        otpOrigin,
        originProcess,
        loginType,
        genericFields,
      ]);
    } catch (error) {
      throw error;
    }
  },
);

export const resendOtpStep1 = createAsyncThunk('authentication/resendOtpStep1', async ({phoneNumber, isText, guid}, thunkApi) => {
  try {
    const createOtpLoginResponse = await ProviderManager.getProviderData('ResendLoginStep1', [phoneNumber, isText, guid]);
    return createOtpLoginResponse;
  } catch (error) {
    throw error;
  }
});

const otpAuthenticationSlice = createSlice({
  name: 'otpAuthentication',
  initialState,
  reducers: {
    resetOtpState: () => initialState,
    validatePhoneHandler: {
      reducer(state, action) {
        state.otpPhoneForm = action.payload;
      },
      prepare(payload) {
        const form = validateFields(lodash.cloneDeep(payload.form));
        if (!form.errors.phone) {
          form.valid = true;
        }
        return {
          payload: form,
        };
      },
    },
    validateOtpCodeHandler: {
      reducer(state, action) {
        state.otpCodeForm = action.payload;
      },
      prepare(payload) {
        const form = validateFields(lodash.cloneDeep(payload.form));
        return {
          payload: form,
        };
      },
    },
    changePhoneInputHandler: {
      reducer(state, action) {
        if (state.showWrongOtpCodeMessage) {
          state.showWrongOtpCodeMessage = false;
        }
        state.otpPhoneForm = action.payload;
      },
      prepare(payload) {
        const name = payload.target.name;
        const value = payload.target.value;
        const form = lodash.cloneDeep(payload.form);
        const validate = validateField(form, name, value);

        if (form.phone.length > 10) {
          form.phone = form.phone.slice(0, 10);
          form.errors.phone = '';
        }

        let isNumber = /^\d+$/.test(form.phone);

        if (form.phone.length < 10 && isNumber) form.errors.phone = '';

        return {
          payload: validate,
        };
      },
    },
    onBlurHandler: {
      reducer(state, action) {
        state[action.payload.form] = action.payload.payload;
      },
      prepare(payload) {
        const name = payload.target.name;
        const value = payload.target.value;
        const form = lodash.cloneDeep(payload.form);
        const validate = validateField(form, name, value);
        return {
          payload: {
            payload: validate,
            form: payload.formName,
          },
        };
      },
    },
    setRestrictedCodeValue: (state, action) => {
      state.restrictedCodeValue = action.payload;
      return state;
    },
    setBlockSource: (state, action) => {
      state.blockSource = action.payload;
    },

    setTermsOfUse: (state, action) => {
      state.termsOfUseConfirmed = action.payload;
      return state;
    },

    setOtpVerifyCode: {
      reducer(state, action) {
        if (action.payload.verifyCode == 'clear') state.otpCodeForm.verifyCode = '';
        else state.otpCodeForm.verifyCode = action.payload.verifyCode;
      },
    },
    clearOtpCode: {
      reducer(state, action) {
        state.form.verifyCode = action.payload.verifyCode;
        state.form.valid = '';
        state.form.isChange = false;
      },
    },
    otpReportStep: {
      reducer(state, action) {
        state.currentStep = action.payload;
      },
    },
    hideOtpErrorMessage: (state, action) => {
      state.showWrongOtpCodeMessage = false;
      return state;
    },
    setRestrictedCodeRequired: (state, action) => {
      state.restrictedCodeRequired = action.payload;
    },
    setTerms: (state, action) => {
      state.terms = action.payload;
    },
    setTicketId: (state, action) => {
      state.ticketId = action.payload;
    },
  },
  extraReducers: {},
});

export const {
  resetOtpState,
  validatePhoneHandler,
  validateOtpCodeHandler,
  changePhoneInputHandler,
  onBlurHandler,
  setOtpVerifyCode,
  clearOtpCode,
  hideOtpErrorMessage,
  setRestrictedCodeValue,
  setTermsOfUse,
  setBlockSource,
  setTerms,
  setTicketId,
  setRestrictedCodeRequired,
} = otpAuthenticationSlice.actions;
export default otpAuthenticationSlice.reducer;
